import { z } from 'zod';

import type { StorefrontProductSummary } from './cms.js';

/**
 * Stories & Highlights — Instagram-style engagement, ADMIN-managed, mobile DISPLAY-only.
 *
 * A story is an ordered stack of image/video media (1080x1920, 9:16) with optional
 * tagged products. Stories can be permanent or expire 24h after publish; drafts and
 * scheduling are supported. Highlights are named, ordered, published collections of
 * stories with a cover (New Arrivals, Offers, …).
 */

/** The 9:16 canvas the admin editor exports to, so media fits the mobile viewer. */
export const STORY_CANVAS = { width: 1080, height: 1920 } as const;
/** Default on-screen time for an image slide (video uses its own length). */
export const STORY_IMAGE_DEFAULT_MS = 5000;

export const STORY_STATUSES = ['draft', 'scheduled', 'published', 'archived'] as const;
export type StoryStatus = (typeof STORY_STATUSES)[number];
export const StoryStatusSchema = z.enum(STORY_STATUSES);

export const STORY_MEDIA_TYPES = ['image', 'video'] as const;
export type StoryMediaType = (typeof STORY_MEDIA_TYPES)[number];
export const StoryMediaTypeSchema = z.enum(STORY_MEDIA_TYPES);

/** How long a published story stays live. */
export const STORY_EXPIRY_MODES = ['permanent', '24h'] as const;
export type StoryExpiryMode = (typeof STORY_EXPIRY_MODES)[number];

/** A story/highlight can link to ONE real entity — the viewer shows a CTA to it. */
export const STORY_LINK_TYPES = ['product', 'category', 'brand'] as const;
export type StoryLinkType = (typeof STORY_LINK_TYPES)[number];

/**
 * A resolved story/highlight link target, served to the viewer so it can render a
 * real CTA card (thumb + label) and navigate. Resolved against the active locale;
 * `null` on the parent DTO when unset or the target no longer exists.
 */
export interface StoryLink {
  type: StoryLinkType;   // 'product' | 'category' | 'brand'
  id: string;            // entity id (product id, category id, or brand id)
  slug: string | null;   // product/category slug when available; null for brand
  label: string;         // localized display name
  image: string | null;  // imageUrl for the CTA card (null if none)
}

// -----------------------------------------------------------------------------
// Admin-facing shapes (full detail, edit surface)
// -----------------------------------------------------------------------------

/** One media slide of a story (admin view — carries the source attachment ids). */
export interface AdminStoryMedia {
  id: string;
  storyId: string;
  attachmentId: string;
  mediaType: StoryMediaType;
  posterAttachmentId: string | null; // video thumbnail frame
  durationMs: number | null;
  sortOrder: number;
  url: string;                        // resolved admin media URL
  posterUrl: string | null;
}

/** A product tagged onto a story (admin view). */
export interface AdminStoryProduct {
  id: string;
  itemId: string;
  name: string | null;   // joined item name for display
  code: string | null;
  sortOrder: number;
  posX: string | null;
  posY: string | null;
}

/** A story with all its media + tagged products (admin editor payload). */
export interface AdminStory {
  id: string;
  title: string | null;
  caption: string | null;
  status: StoryStatus;
  scheduledAt: string | null;
  publishedAt: string | null;
  expiresAt: string | null; // null = permanent (or not yet published)
  sortOrder: number;
  /** Times this story has been opened on the storefront / in the app. */
  views: number;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  media: AdminStoryMedia[];
  products: AdminStoryProduct[];
  linkType: StoryLinkType | null; // linked entity kind (editor pre-fill)
  linkRefId: string | null;       // linked entity id (raw; resolved on the storefront)
}

/**
 * One frame of a highlight's OWN media. Deliberately the same shape as
 * AdminStoryMedia so the viewer that plays a story can play a highlight without
 * learning a second vocabulary — only the parent id differs.
 */
export interface HighlightMedia {
  id: string;
  highlightId: string;
  attachmentId: string;
  mediaType: StoryMediaType;
  posterAttachmentId: string | null;
  durationMs: number | null;
  sortOrder: number;
  url: string;
  posterUrl: string | null;
}

/** A highlight (admin view). Owns its media; no longer built from stories. */
export interface Highlight {
  id: string;
  title: string;
  coverAttachmentId: string | null;
  coverUrl: string | null;
  sortOrder: number;
  isPublished: boolean;
  /** This highlight's own frames, in play order. */
  media: HighlightMedia[];
  createdAt: string;
  updatedAt: string;
  linkType: StoryLinkType | null; // linked entity kind (editor pre-fill)
  linkRefId: string | null;       // linked entity id (raw; resolved on the storefront)
}

// -----------------------------------------------------------------------------
// Admin write schemas
// -----------------------------------------------------------------------------

/**
 * Entity link fields shared by story + highlight write schemas.
 * `linkRefId` is validated as a non-empty string (ids are whatever /items,
 * /categories, /brands return — existence is checked server-side). Passing
 * `linkType: null` clears the link; omitting the fields leaves it unchanged.
 */
const linkType = z.enum(STORY_LINK_TYPES).nullable().optional();
const linkRefId = z.string().min(1).nullable().optional();

export const StoryCreateSchema = z.object({
  title: z.string().max(200).nullish(),
  caption: z.string().max(1000).nullish(),
  linkType,
  linkRefId,
});
export type StoryCreate = z.infer<typeof StoryCreateSchema>;

export const StoryUpdateSchema = z.object({
  title: z.string().max(200).nullish(),
  caption: z.string().max(1000).nullish(),
  linkType,
  linkRefId,
});
export type StoryUpdate = z.infer<typeof StoryUpdateSchema>;

/**
 * Publish (or schedule) a story. `scheduledAt` in the future ⇒ status `scheduled`
 * (goes live automatically once the time passes); omitted/past ⇒ published now.
 * `expiry` decides whether an expiry stamp is set (`24h`) or not (`permanent`).
 */
export const StoryPublishSchema = z.object({
  expiry: z.enum(STORY_EXPIRY_MODES).default('permanent'),
  scheduledAt: z.string().datetime().nullish(),
});
export type StoryPublish = z.infer<typeof StoryPublishSchema>;

/** Replace the ordered set of tagged products (array order = display order). */
export const StoryProductsSchema = z.object({
  itemIds: z.array(z.string().uuid()).max(50),
});
export type StoryProducts = z.infer<typeof StoryProductsSchema>;

/** Patch one media row (set the video poster frame, or per-slide duration). */
export const StoryMediaPatchSchema = z.object({
  posterAttachmentId: z.string().uuid().nullish(),
  durationMs: z.number().int().min(500).max(60_000).nullish(),
});
export type StoryMediaPatch = z.infer<typeof StoryMediaPatchSchema>;

/** Reorder rows by an explicit id list (stories, media, or highlights). */
export const IdOrderSchema = z.object({ ids: z.array(z.string().uuid()).max(500) });
export type IdOrder = z.infer<typeof IdOrderSchema>;

export const HighlightCreateSchema = z.object({
  title: z.string().min(1).max(200),
  coverAttachmentId: z.string().uuid().nullish(),
  linkType,
  linkRefId,
});
export type HighlightCreate = z.infer<typeof HighlightCreateSchema>;

export const HighlightUpdateSchema = z.object({
  title: z.string().min(1).max(200).optional(),
  coverAttachmentId: z.string().uuid().nullish(),
  isPublished: z.boolean().optional(),
  linkType,
  linkRefId,
});
export type HighlightUpdate = z.infer<typeof HighlightUpdateSchema>;

/** Reorder a highlight's own media frames. */
export const HighlightMediaReorderSchema = z.object({
  mediaIds: z.array(z.string().uuid()).max(200),
});
export type HighlightMediaReorder = z.infer<typeof HighlightMediaReorderSchema>;

/** Edit one frame (how long it holds on screen). */
export const HighlightMediaPatchSchema = z.object({
  durationMs: z.number().int().min(1000).max(60_000).nullable().optional(),
});
export type HighlightMediaPatch = z.infer<typeof HighlightMediaPatchSchema>;

// -----------------------------------------------------------------------------
// Public storefront shapes (mobile display-only)
// -----------------------------------------------------------------------------

export interface StorefrontStoryMedia {
  type: StoryMediaType;
  url: string;
  posterUrl: string | null;
  durationMs: number;
}

/** A live story served to the mobile viewer. */
export interface StorefrontStory {
  id: string;
  title: string | null;
  caption: string | null;
  coverUrl: string | null;               // first media (or its poster) — the tray avatar
  media: StorefrontStoryMedia[];
  products: StorefrontProductSummary[];   // tap → product preview
  link: StoryLink | null;                 // resolved entity CTA (null when unset/missing)
}

/** A published highlight for the mobile tray. */
export interface StorefrontHighlight {
  id: string;
  title: string;
  coverUrl: string | null;
  /**
   * The frames to play. EMPTY is legitimate: a highlight with only a `link` is a
   * shortcut tile, and the app navigates instead of opening the viewer.
   */
  media: StorefrontStoryMedia[];
  link: StoryLink | null;                 // resolved entity CTA (null when unset/missing)
}
